import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import Helmet from 'react-helmet';

import { CUSTOM_PRINT_TYPE } from '../../../../constants/pro-custom-prints';
import useCustomPrintConfig from './useCustomPrintConfig';
import CustomPrintConfiguratorPhotoSelectorModal from '../components/CustomPrintConfiguratorPhotoSelectorModal';
import { CustomPrintConfigurationPhotos } from '../../../../graphql/queries/custom-prints';
import { createCustomPrintCandidatePhotoGroups } from '../helpers';
import CardElementPhotoImage from '../../../../services/cards/components/CardElementPhoto/CardElementPhotoImage';
import CardElementPlaceholder from '../../../../services/cards/components/CardElementPlaceholder';
import CameraIcon from '../../../icons/CameraIcon';
import { getCardPhotoElementConfigurationImageUrl } from '../../../../services/cards/components/CardElementPhoto/helpers';
import CardElementPhotoControl from '../../../../services/cards/components/CardElementPhoto/CardElementPhotoControl/CardElementPhotoControl';
import FormGroup from '../../FormGroup';
import { getAsset } from '../../../../util/assets';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';

const propTypes = {
  className: PropTypes.string,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photos_no: PropTypes.number.isRequired,
    name: PropTypes.string,
    custom_print: PropTypes.shape({
      custom_print_caption_enabled: PropTypes.bool,
      type: PropTypes.oneOf(Object.values(CUSTOM_PRINT_TYPE)),
    }),
    selected_identity: PropTypes.shape({
      id: PropTypes.string,
    }),
    identity: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    custom_print_caption_enabled: PropTypes.bool,
  }).isRequired,
  candidatePhotoGroups: PropTypes.array,
  renderSubmit: PropTypes.func,
  initialConfiguration: PropTypes.shape({
    custom_print_caption: PropTypes.string,
    photos: PropTypes.array,
  }),
  viewOnly: PropTypes.bool,
  imageSizeCode: PropTypes.string,
};

const CustomPrintPlaqueConfigurator = ({
  className,
  albumCustomPrint,
  candidatePhotoGroups,
  renderSubmit,
  initialConfiguration,
  viewOnly,
  imageSizeCode = IMAGE_SIZE_CODE.MEDIUM,
  watermarkSettings,
}) => {
  const classes = classNames({
    'custom-print-plaque-configurator': true,
    'custom-print-plaque-configurator-view-only': viewOnly,
    [className]: !!className,
  });
  const positions = Array.from({ length: albumCustomPrint.photos_no });
  const {
    config,
    onPhotoEdit,
    onPhotoSelectTrigger,
    onPhotoSelect,
    onPhotoSelectCancel,
    selectedPhoto,
    setPhotoEditing,
    onCaptionEdit,
  } = useCustomPrintConfig({ albumCustomPrint, initialConfiguration });

  return (
    <Fragment>
      {!viewOnly && (
        <Helmet>
          <link
            href="https://fonts.googleapis.com/css?family=Kalam"
            rel="stylesheet"
          />
        </Helmet>
      )}

      {!viewOnly && (
        <img
          src={getAsset('/confetti-transparent.png')}
          alt="Confetti"
          className="custom-print-plaque-configurator-confetti"
        />
      )}

      <div className={classes}>
        {!viewOnly && (
          <Fragment>
            <p className="text-black text-dark mb-0">Choose your photos!</p>
            <p className="text-sm">
              Press on each slot to select your fav pic or edit an existing
              photo.{' '}
              <strong>Your photos will be printed in high quality.</strong>
            </p>
          </Fragment>
        )}

        <div className="custom-print-plaque-configurator-template mb-3">
          {positions.map((_, index) => {
            const currentPhoto = config.photos.find(
              photo => photo.position === index,
            );
            const imageUrl = getCardPhotoElementConfigurationImageUrl(
              currentPhoto,
              imageSizeCode,
            );

            return (
              <Fragment key={index}>
                <a
                  href="#"
                  onClick={event => {
                    event.preventDefault();

                    if (imageUrl) {
                      setPhotoEditing(true);
                    }
                    onPhotoSelectTrigger(index);
                  }}
                  className={classNames({
                    'custom-print-plaque-photo': true,
                    'has-photo': !!imageUrl,
                  })}
                >
                  {imageUrl ? (
                    <CardElementPhotoImage
                      cropbox={currentPhoto.cropbox}
                      src={imageUrl}
                      watermarkSettings={watermarkSettings}
                    />
                  ) : (
                    <CardElementPlaceholder
                      icon={
                        <CameraIcon
                          color="#0d84fe"
                          id={`camera-icon-for-${index}`}
                        />
                      }
                    />
                  )}
                </a>
              </Fragment>
            );
          })}

          {!viewOnly && (
            <p className="custom-print-plaque-title">
              Event name and date will be printed here
            </p>
          )}
        </div>

        {albumCustomPrint.custom_print?.custom_print_caption_enabled &&
          !viewOnly && (
            <Fragment>
              <FormGroup
                id="custom-print-caption"
                className="mb-0"
                name="caption"
                labelType="expanded"
                label="Enter your caption"
                labelSrOnly
                placeholder="Enter a small caption (e.g. your name)"
                value={config.custom_print_caption}
                onChange={event => {
                  const { target } = event;
                  const { value } = target;
                  onCaptionEdit(value);
                }}
              />
              <p className="text-sm lh-normal mt-1 mb-3">
                The photographer will decide the best location on the print for
                your caption.
              </p>
            </Fragment>
          )}

        {renderSubmit?.(config)}
      </div>

      {!viewOnly && (
        <Fragment>
          <CustomPrintConfiguratorPhotoSelectorModal
            defaultPhotoGroups={candidatePhotoGroups}
            onPhotoConfirm={onPhotoSelect}
            selectedPhoto={selectedPhoto}
            forceOpen={!!selectedPhoto && !config.editingPhoto}
            onClose={onPhotoSelectCancel}
          />

          <CardElementPhotoControl
            forceOpen={config.editingPhoto}
            onClose={() => setPhotoEditing(false)}
            onPhotoChange={() => setPhotoEditing(false)}
            element={{
              cardElement: {
                side: '',
                // TODO These are hardcoded values for JKP needs only.
                // Should become dynamic if we're going to continue using it.
                frame: {
                  width: selectedPhoto?.position === 0 ? 1 : 0.3125,
                  height: selectedPhoto?.position === 0 ? 1 : 0.35,
                },
              },
              cardElementConfiguration: {
                cropbox: selectedPhoto?.cropbox,
                photo: selectedPhoto?.photo,
              },
            }}
            onChange={cropbox =>
              onPhotoEdit({ cropbox }, selectedPhoto.position)
            }
            cardWidth={8}
            cardHeight={10}
            imageSizeCode={IMAGE_SIZE_CODE.MEDIUM}
            watermarkSettings={watermarkSettings}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

CustomPrintPlaqueConfigurator.propTypes = propTypes;

const withAlbumCustomPrintCandidatePhotos = graphql(
  CustomPrintConfigurationPhotos,
  {
    options: ({ albumCustomPrint, identity }) => ({
      variables: {
        albumCustomPrintId: albumCustomPrint.id,
        identityId: identity.id,
      },
    }),
    props: ({ data: { customPrintConfigurationPhotos, fetchMore } }) => {
      return {
        candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: customPrintConfigurationPhotos,
          mappings: [
            {
              id: 'matches',
              label: 'Matches',
              path: 'matched_photos_extended',
              mapper: record => record.photo,
            },
            {
              id: 'favorites',
              label: 'Favorites',
              path: 'favorited_photos',
            },
            {
              id: 'allPhotos',
              label: 'All Photos',
              path: 'all_photos',
            },
          ],
          fetchMore,
        }),
      };
    },
    skip: ({ albumCustomPrint, identity, viewOnly }) =>
      !albumCustomPrint || !identity || viewOnly,
  },
);

export default compose(withAlbumCustomPrintCandidatePhotos)(
  CustomPrintPlaqueConfigurator,
);
